function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
class PollingController {
    registerConfig(key, config) {
        this.pollConfigs.set(key, config);
        this.recalculateEffectiveConfigs();
        this.ensureClockSubscription();
    }
    unregisterConfig(key) {
        this.pollConfigs.delete(key);
        this.recalculateEffectiveConfigs();
        this.cleanupIfNeeded();
    }
    getEffectiveInterval() {
        return this.effectiveInterval;
    }
    getEffectiveExpectedErrors() {
        return this.effectiveExpectedErrors;
    }
    getEffectiveRetryConfig() {
        return this.effectiveRetryConfig;
    }
    handleFetchResult(result) {
        if (!result.hasClientErrors && !result.hasUnexpectedErrors) {
            this.handleSuccess();
            return;
        }
        if (result.hasClientErrors) {
            this.handleClientError();
            return;
        }
        if (result.hasUnexpectedErrors) {
            this.handleUnexpectedError();
        }
    }
    resetTimer() {
        this.ticksSinceLastPoll = 0;
    }
    cleanup() {
        if (this.clockUnsubscribe) {
            this.clockUnsubscribe();
            this.clockUnsubscribe = null;
        }
        this.pollingState = 'idle';
        this.ticksSinceLastPoll = 0;
        this.clearRetryState();
        this.pollConfigs.clear();
        this.effectiveInterval = null;
        this.effectiveExpectedErrors = [];
        this.effectiveRetryConfig = null;
    }
    recalculateEffectiveConfigs() {
        if (this.pollConfigs.size === 0) {
            this.effectiveInterval = null;
            this.effectiveExpectedErrors = [];
            this.effectiveRetryConfig = null;
            return;
        }
        const configs = Array.from(this.pollConfigs.values());
        this.effectiveInterval = Math.min(...configs.map((c)=>c.interval));
        const firstConfigErrors = configs[0].expectedErrors;
        this.effectiveExpectedErrors = firstConfigErrors.filter((error)=>configs.every((c)=>c.expectedErrors.includes(error)));
        const retryConfigs = configs.map((c)=>c.retryConfig);
        this.effectiveRetryConfig = {
            maxRetries: Math.max(...retryConfigs.map((r)=>r.maxRetries)),
            delay: Math.min(...retryConfigs.map((r)=>r.delay)),
            maxDelay: Math.min(...retryConfigs.map((r)=>{
                var _r_maxDelay;
                return (_r_maxDelay = r.maxDelay) !== null && _r_maxDelay !== void 0 ? _r_maxDelay : Number.MAX_SAFE_INTEGER;
            }))
        };
    }
    ensureClockSubscription() {
        if (this.clockUnsubscribe || !this.pollingClock) {
            return;
        }
        this.clockUnsubscribe = this.pollingClock.subscribe({
            onTick: (tickTime)=>this.handleTick(tickTime),
            onVisibilityResume: (elapsedMs)=>this.handleVisibilityResume(elapsedMs)
        });
        this.pollingState = 'polling';
    }
    handleTick(_tickTime) {
        if (this.pollingState !== 'polling') {
            return;
        }
        const interval = this.effectiveInterval;
        if (interval === null) {
            return;
        }
        this.ticksSinceLastPoll += 1000;
        if (this.ticksSinceLastPoll >= interval) {
            this.executePoll();
        }
    }
    handleVisibilityResume(elapsedMs) {
        if (this.pollingState !== 'polling') {
            return;
        }
        const interval = this.effectiveInterval;
        if (interval === null) {
            return;
        }
        if (this.ticksSinceLastPoll + elapsedMs >= interval) {
            this.executePoll();
        } else {
            this.ticksSinceLastPoll += elapsedMs;
        }
    }
    executePoll() {
        if (this.callbacks.isCurrentlyFetching()) {
            return;
        }
        this.ticksSinceLastPoll = 0;
        this.callbacks.triggerRefetch();
    }
    cleanupIfNeeded() {
        if (this.pollConfigs.size === 0 && this.clockUnsubscribe) {
            this.clockUnsubscribe();
            this.clockUnsubscribe = null;
            this.pollingState = 'idle';
            this.ticksSinceLastPoll = 0;
            this.clearRetryState();
        }
    }
    handleSuccess() {
        this.clearRetryState();
        if (this.pollingState === 'retrying') {
            this.pollingState = 'polling';
        }
    }
    handleUnexpectedError() {
        if (!this.effectiveRetryConfig) {
            return;
        }
        if (!this.retryState) {
            this.retryState = {
                attempt: 0,
                config: this.effectiveRetryConfig,
                timeoutId: null
            };
        }
        this.retryState.attempt += 1;
        if (this.retryState.attempt > this.effectiveRetryConfig.maxRetries) {
            this.pollingState = 'stopped';
            this.clearRetryState();
            return;
        }
        this.pollingState = 'retrying';
        this.scheduleRetry();
    }
    handleClientError() {
        this.pollingState = 'stopped';
        this.clearRetryState();
    }
    scheduleRetry() {
        if (!this.retryState || !this.effectiveRetryConfig) {
            return;
        }
        const { delay, maxDelay } = this.effectiveRetryConfig;
        const attempt = this.retryState.attempt;
        const currentDelay = Math.min(delay * Math.pow(1.5, attempt - 1), maxDelay !== null && maxDelay !== void 0 ? maxDelay : Number.MAX_SAFE_INTEGER);
        this.retryState.timeoutId = setTimeout(()=>{
            this.executeRetry();
        }, currentDelay);
    }
    executeRetry() {
        if (this.retryState) {
            this.retryState.timeoutId = null;
        }
        this.callbacks.triggerRefetch();
    }
    clearRetryState() {
        var _this_retryState;
        if ((_this_retryState = this.retryState) === null || _this_retryState === void 0 ? void 0 : _this_retryState.timeoutId) {
            clearTimeout(this.retryState.timeoutId);
        }
        this.retryState = null;
    }
    constructor(pollingClock, callbacks){
        _define_property(this, "pollingClock", void 0);
        _define_property(this, "callbacks", void 0);
        _define_property(this, "pollConfigs", new Map());
        _define_property(this, "effectiveInterval", null);
        _define_property(this, "effectiveExpectedErrors", []);
        _define_property(this, "effectiveRetryConfig", null);
        _define_property(this, "clockUnsubscribe", null);
        _define_property(this, "ticksSinceLastPoll", 0);
        _define_property(this, "pollingState", 'idle');
        _define_property(this, "retryState", null);
        this.pollingClock = pollingClock;
        this.callbacks = callbacks;
    }
}
export { PollingController as default };

//# sourceMappingURL=PollingController.js.map